// ============================================================================
// SCHOOL — the machine-made half of the domain, and the rules that keep it honest.
//
// This file exists as its own module rather than as another block in types.ts for one reason:
// everything here is governed by a single product promise, and keeping the promise next to the
// types it constrains is what stops the two drifting apart.
//
// THE PROMISE (docs/design/handoff/README.md → Overview, and CLAUDE.md → Product identity):
// the app never fakes knowledge.
//   · A grade the instructor hid is a DIFFERENT STATE from a grade Canvas has not computed yet,
//     which is a different state again from a grade that exists. Three states, three sentences,
//     never a blank.
//   · Everything a model wrote carries { model, sourceRef, generatedAt }, and an artifact that
//     cannot say where it came from DOES NOT RENDER. Not a warning, not a placeholder — absence.
//     A summary with no provenance is indistinguishable from something the professor said.
//   · Fields the Canvas API cannot supply are not in these types at all. There is no
//     `waitlistPosition` here and there never will be (README §3, "Absent by design").
// ============================================================================

import { formatShortDate } from './dates.js'
import type { Assignment, Course, ISODate, ISOTimestamp, Lecture } from './types.js'

// ── Provenance ──────────────────────────────────────────────────────────────

/**
 * Where a machine-made claim came from. Every field is required, and every field is required for
 * a reason: without `model` the reader cannot weigh it, without `sourceRef` they cannot check it,
 * and without `generatedAt` they cannot tell whether it predates the thing it describes.
 */
export interface AiProvenance {
  /** 'claude-haiku-4.5', 'Whisper large-v3' — the thing that actually produced the text. */
  model: string
  /** What it was made FROM: a transcript id, a file name. Never a category, always a locator. */
  sourceRef: string
  generatedAt: ISOTimestamp
}

/** Anything a model wrote. The constraint is structural: you cannot have one without provenance. */
export interface AiArtifact {
  provenance: AiProvenance
}

/**
 * Is this artifact allowed on screen?
 *
 * Called on the render path, not just in tests, because the type system stops at the network
 * boundary: a Worker response, a cached payload from an older schema, or a half-written row can
 * all arrive shaped like an artifact and carrying nothing. `undefined` is a shape, not a claim.
 */
export function hasProvenance<T extends AiArtifact>(artifact: T | null | undefined): artifact is T {
  if (!artifact) return false
  const p = artifact.provenance as AiProvenance | undefined | null
  if (!p || typeof p !== 'object') return false
  return (
    typeof p.model === 'string' &&
    p.model.trim().length > 0 &&
    typeof p.sourceRef === 'string' &&
    p.sourceRef.trim().length > 0 &&
    typeof p.generatedAt === 'string' &&
    p.generatedAt.trim().length > 0
  )
}

/** The list form. An unlabelled item is dropped, never rendered bare and never counted. */
export function onlyProvenanced<T extends AiArtifact>(artifacts: readonly T[] | undefined): T[] {
  return (artifacts ?? []).filter((a) => hasProvenance(a))
}

// ── Grades: hidden is not missing ───────────────────────────────────────────

/**
 * The three honest answers to "what is my grade in this course", as a discriminated union so the
 * view cannot accidentally collapse two of them into one blank cell.
 *
 *   hidden  — the instructor set Canvas's `hide_final_grades`. A score exists; we are not allowed
 *             to see it. The UI says so in words.
 *   none    — Canvas returned no score, because nothing is graded yet. Also said in words.
 *   score   — a real `computed_current_score`, and optionally Canvas's own
 *             `computed_current_grade` letter. The letter is NEVER derived from the percentage:
 *             grading scales are per-course, and inventing 'B+' from 88.4 is exactly the kind of
 *             plausible fabrication this product exists to not do.
 */
export type CourseGrade =
  | { kind: 'hidden' }
  | { kind: 'none' }
  | { kind: 'score'; percent: number; letter: string | null }

export function courseGrade(course: Course): CourseGrade {
  if (course.gradeHidden) return { kind: 'hidden' }
  if (course.gradeSoFar === null) return { kind: 'none' }
  return { kind: 'score', percent: course.gradeSoFar, letter: course.gradeLetter }
}

/** '88.4% B+' · '88.4%'. Only ever called on the `score` branch. */
export function gradeChipLabel(grade: Extract<CourseGrade, { kind: 'score' }>): string {
  const pct = `${grade.percent.toFixed(1)}%`
  return grade.letter ? `${pct} ${grade.letter}` : pct
}

// ── Deadlines: dated is not the same as existing ────────────────────────────
//
// Canvas assignments are usually UNDATED. That is a fact about how professors use bCourses, not
// a gap in the sync, and it puts the app in front of the same trap the grade block does: an
// undated assignment quietly dropped from a list is the app claiming work does not exist.
//
// So the split is in the type system. A runway, a countdown or a "due this week" count needs a
// date to mean anything, and takes `DatedAssignment`; a course's own table takes `Assignment`
// and renders the undated ones with the reason in the DUE cell.

/** An assignment Canvas gave a deadline. The only thing a time-ordered view may take. */
export type DatedAssignment = Assignment & { dueAt: ISOTimestamp }

export function isDatedAssignment(assignment: Assignment): assignment is DatedAssignment {
  return typeof assignment.dueAt === 'string' && assignment.dueAt.length > 0
}

/** The ones a runway can place. Everything else is still real; it is just not on a timeline. */
export function datedAssignments(
  assignments: readonly Assignment[] | undefined,
): DatedAssignment[] {
  return (assignments ?? []).filter(isDatedAssignment)
}

/** The ones Canvas gave no deadline. Counted so a screen can say how many it is not showing. */
export function undatedAssignments(assignments: readonly Assignment[] | undefined): Assignment[] {
  return (assignments ?? []).filter((a) => !isDatedAssignment(a))
}

// ── Announcements ───────────────────────────────────────────────────────────

export interface Announcement {
  id: string
  courseId: string
  courseCode: string
  /** Who posted it. Canvas gives a display name; null when it gives nothing. */
  author: string | null
  title: string
  body: string
  postedAt: ISOTimestamp
  read: boolean
}

// ── Syllabus: facts read out of a PDF, and labelled as such ─────────────────

export interface SyllabusComponent {
  label: string
  /** Share of the final grade, 0–100. */
  weightPct: number
}

export interface SyllabusExam {
  label: string
  date: ISODate
  /** 'in class' · '8:00am' — how the syllabus itself words it. */
  detail: string
}

export interface SyllabusWeek {
  /** 1-based week of instruction. */
  week: number
  title: string
}

/**
 * The syllabus PDF, after a model read it. Every number on the panel came out of a document
 * rather than out of Canvas, so the whole panel carries one provenance chip: "extracted from
 * syllabus PDF · <date>". An extract with no provenance does not render.
 */
export interface SyllabusExtract extends AiArtifact {
  courseId: string
  /** The file it was read out of, as the owner would recognise it. */
  sourceFile: string
  gradingBreakdown: SyllabusComponent[]
  exams: SyllabusExam[]
  weeklyTopics: SyllabusWeek[]
}

// ── Course files: what bCourses actually holds ──────────────────────────────
//
// The catalog `GET /api/school/files` serves, and the file half of the syllabus card. NOTHING
// HERE IS AI. A `SyllabusExtract` above is a model's reading and wears a provenance chip; a
// `CourseFile` is a listing and its `text` is a transcription, so neither carries one and neither
// should ever be drawn as though it did.

/**
 * Whether anything read this file, and — when something tried and failed — why not.
 *
 * FOUR STATES BECAUSE THE UI HAS FOUR SENTENCES. Collapsing `failed` into `not_attempted` would
 * turn "your syllabus is there and could not be read" into silence, which is exactly the honest-
 * degradation failure the design system forbids.
 */
export type FileExtractionStatus = 'not_attempted' | 'extracted' | 'failed' | 'too_large'

export interface CourseFile {
  id: string
  /** The Canvas file id. */
  externalId: string
  courseId: string
  courseCode: string | null
  /** The stored name: 'CogSci1_F26_Syllabus.pdf'. */
  filename: string
  /** What bCourses SHOWS — the string the owner has seen, so the one the UI prints. */
  displayName: string
  contentType: string | null
  sizeBytes: number | null
  /** 'course files/Course Information'. Null when the folder listing failed. */
  folderPath: string | null
  /** Canvas's download link, carrying its own verifier. It expires; every sync brings a fresh one. */
  url: string | null
  /** Canvas's `updated_at` for the file. Null when it gave none. */
  updatedAt: ISOTimestamp | null
  extraction: FileExtractionStatus
  /** Why it could not be read, in words. Null unless `extraction` says otherwise. */
  extractionError: string | null
  extractedAt: ISOTimestamp | null
  /** Pages the reader saw. Null unless a PDF was actually opened. */
  pages: number | null
  /** Length of the extracted text. Null when there is none — never 0 standing in for absent. */
  characters: number | null
}

/**
 * The file a course's syllabus lives in, and the text a PDF reader got out of it.
 *
 * `text` is NULL whenever `file.extraction` is not `extracted`, and then `file.extractionError`
 * is the sentence to show. Never an empty string standing in for "could not read".
 */
export interface SyllabusDocument {
  file: CourseFile
  text: string | null
}

// ── Lecture study set ───────────────────────────────────────────────────────

export interface LectureSummary extends AiArtifact {
  lectureId: string
  bullets: string[]
  keyConcepts: string[]
}

export interface Flashcard extends AiArtifact {
  id: string
  lectureId: string
  prompt: string
  answer: string
  /**
   * The moment in the recording the card was drawn from, so the card can cite itself
   * ("generated from this lecture · 18:52"). Null when the card came from the summary rather
   * than from one place in the transcript — and then no timestamp is shown, not a guessed one.
   */
  sourceMs: number | null
  dueForReview: boolean
}

export interface PracticeQuestion extends AiArtifact {
  id: string
  lectureId: string
  question: string
  answer: string
  /** Whether the owner has already worked this one. Session progress, not a score. */
  answered: boolean
}

// ── Derived, never stored ───────────────────────────────────────────────────

/**
 * 'cogsci-175' from 'COGSCI 175'. The handoff routes are `/school/:courseCode`; a code with a
 * space in it would ride through the URL percent-encoded and come back looking like a mistake,
 * so the code is slugged on the way out and matched on the way back in.
 */
export function courseSlug(code: string): string {
  return code.toLowerCase().replace(/\s+/g, '-')
}

export function matchesCourseSlug(course: Course, slug: string): boolean {
  return courseSlug(course.code) === slug.toLowerCase()
}

// ── Naming and addressing a lecture that may have neither number nor name ────
//
// `Lecture.index` and `Lecture.title` are both nullable, and both are usually null in production:
// nothing in the recording pipeline numbers a lecture, and only the surfaces that know a filename
// name one (apps/web/src/lib/data/types.ts → CapturedLecture explains why). Every screen that
// prints or links to a lecture goes through these two functions, so there is exactly one place
// where "no number" is answered — and no template string anywhere can quietly render
// 'Lecture null · null'.

/**
 * What a lecture calls itself on screen. Never invents a number, never invents a name.
 *
 *   'Lecture 5 · Attention and memory'   both known
 *   'Lecture 5'                          numbered, unheard
 *   'Voice 013.m4a'                      named by the file it arrived as
 *   'Recording · Sep 10'                 neither — the day it happened, which is always true
 */
export function lectureLabel(lecture: Pick<Lecture, 'index' | 'title' | 'date'>): string {
  const number = lecture.index === null ? null : `Lecture ${lecture.index}`
  const title = lecture.title?.trim() ? lecture.title.trim() : null
  if (number && title) return `${number} · ${title}`
  if (number) return number
  if (title) return title
  return `Recording · ${formatShortDate(lecture.date)}`
}

/**
 * The SHORT form, for a citation: 'COGSCI 175 · Lecture 5 · 13:04'.
 *
 * Separate from `lectureLabel` because a citation is a pointer and not a heading — it sits
 * between a course code and a timestamp, and hanging the lecture's whole title in the middle of
 * that turns "where this came from" into a sentence nobody can scan. Same refusals: the number
 * when there is one, the recording's own name when there is not, the day when there is neither.
 */
export function lectureCitation(lecture: Pick<Lecture, 'index' | 'title' | 'date'>): string {
  if (lecture.index !== null) return `Lecture ${lecture.index}`
  if (lecture.title?.trim()) return lecture.title.trim()
  return `Recording · ${formatShortDate(lecture.date)}`
}

/**
 * The path segment that addresses one lecture under its course: `/school/cogsci-1/lecture/<ref>`.
 *
 * The number when there is one — it is what a citation says out loud and what the owner would
 * type — and the row's own id when there is not. An id in the URL is ugly and correct; a made-up
 * lecture number in the URL is tidy and wrong, and it would point at a different lecture the
 * moment a real one is numbered.
 */
export function lectureRef(lecture: Pick<Lecture, 'index' | 'id'>): string {
  return lecture.index === null ? lecture.id : String(lecture.index)
}

/** The other half of `lectureRef`: does this lecture answer to that path segment? */
export function matchesLectureRef(lecture: Pick<Lecture, 'index' | 'id'>, ref: string): boolean {
  return lectureRef(lecture) === ref
}
